/**
 * AuthGatekeeper - Global Auth Loading State
 * 
 * CRITICAL: This component MUST render BEFORE any other content.
 * It shows a full-screen loading animation until Firebase auth check completes.
 * 
 * Flow:
 * 1. App opens → Show Ascending Moon loading animation
 * 2. Firebase onAuthStateChanged fires
 * 3. IF user found → Pass authenticated state to children
 * 4. IF no user → Pass guest state to children
 * 5. ONLY THEN render children (OnboardingFlow)
 * 
 * This prevents the "race condition" where users see onboarding before auth check.
 */
import { useState, useEffect, createContext, useContext } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { motion, AnimatePresence } from 'framer-motion';
import { auth } from '@/lib/firebase';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// MODULE-LEVEL STATE - persists across React StrictMode re-renders
// This is the KEY to fixing the race condition
let moduleAuthResolved = false;
let moduleAuthResult = null; // Will hold { user, profile, isAuthenticated } once resolved

// Create auth context for global access
const AuthContext = createContext({
    user: null,
    profile: null,
    isAuthenticated: false,
    isLoading: true,
    logout: () => {},
});

export const useAuthGatekeeper = () => useContext(AuthContext);

/**
 * AuthGatekeeper Provider Component
 * 
 * CRITICAL: This component MUST show the loading screen on initial render.
 * Uses module-level state to track auth resolution across React StrictMode remounts.
 * 
 * The HTML page has an initial loader (#initial-loader) that shows BEFORE React loads.
 * This component hides that loader and shows the React-rendered content once auth resolves.
 * 
 * NOTE: The actual loading screen is rendered via static HTML in public/index.html
 * (#initial-loader) which displays BEFORE React even loads for zero-latency display.
 */
export const AuthGatekeeper = ({ children }) => {
    // ALWAYS start with loading=true on initial render
    // This ensures the loading screen shows immediately before any async work
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    
    // Helper to hide the initial HTML loader and show React content
    const hideInitialLoader = () => {
        document.body.classList.add('app-ready');
        // Remove the initial loader from DOM after fade-out animation
        setTimeout(() => {
            const initialLoader = document.getElementById('initial-loader');
            if (initialLoader) {
                initialLoader.remove();
            }
        }, 600);
    };

    useEffect(() => {
        // BRIDGE THE HANDSHAKE: Check for session_token FIRST
        // This ensures we don't fire 401 requests before auth is ready
        const existingToken = localStorage.getItem('session_token');
        const googleAuthPending = localStorage.getItem('google_auth_pending');
        
        console.log('🔐 AuthGatekeeper: Starting auth check...', {
            hasToken: !!existingToken,
            googleAuthPending: !!googleAuthPending
        });
        
        // If Google auth is pending, skip the guest session shortcut
        // Let Firebase handle the redirect via onAuthStateChanged in useFirebaseAuth
        if (googleAuthPending) {
            console.log('🔐 AuthGatekeeper: Google auth pending, letting Firebase process redirect...');
            // DON'T return early - let the Firebase auth listener below handle it
        }
        
        // IMMEDIATE GUEST SESSION CHECK - Read from localStorage BEFORE Firebase auth
        // Only do this if NOT waiting for Google auth redirect
        const onboardingData = localStorage.getItem('astral_belonging_onboarding');
        if (onboardingData && !googleAuthPending) {
            try {
                const parsed = JSON.parse(onboardingData);
                if (parsed.showMarketplace && parsed.userData?.birthDate) {
                    // Check if this is an authenticated user returning or a guest
                    if (existingToken) {
                        console.log('🔐 AuthGatekeeper: Found authenticated user with token, will validate...');
                        // Don't skip - let Firebase auth validate the token
                    } else {
                        console.log('🔐 AuthGatekeeper: Found valid guest session in localStorage');
                        // Guest user has completed onboarding - allow immediate access
                        moduleAuthResolved = true;
                        moduleAuthResult = { user: null, profile: null, isAuthenticated: false };
                        setIsLoading(false);
                        hideInitialLoader();
                        console.log('👤 AuthGatekeeper: Guest session restored from localStorage immediately');
                        return;
                    }
                }
            } catch (e) {
                console.warn('Failed to parse onboarding data:', e);
            }
        }
        
        // If we already resolved auth globally, immediately apply cached result
        if (moduleAuthResolved && moduleAuthResult) {
            console.log('🔐 AuthGatekeeper: Using cached auth result');
            setUser(moduleAuthResult.user);
            setProfile(moduleAuthResult.profile);
            setIsAuthenticated(moduleAuthResult.isAuthenticated);
            // Brief delay for smooth transition even with cached result
            setTimeout(() => {
                setIsLoading(false);
                hideInitialLoader();
            }, 150);
            return;
        }
        
        console.log('🔐 AuthGatekeeper: Initializing auth check...');
        
        let isFirstCallback = true;
        let timeoutId = null;
        let cancelled = false;
        
        const resolveAuth = (result) => {
            if (cancelled || moduleAuthResolved) return;
            
            moduleAuthResolved = true;
            moduleAuthResult = result;
            
            setUser(result.user);
            setProfile(result.profile);
            setIsAuthenticated(result.isAuthenticated);
            
            // Small delay for smooth animation, then hide initial loader
            setTimeout(() => {
                if (!cancelled) {
                    setIsLoading(false);
                    hideInitialLoader();
                    console.log('🚀 AuthGatekeeper: Ready to render app', result.isAuthenticated ? '(authenticated)' : '(guest)');
                }
            }, 300);
        };
        
        // Listen for Firebase auth state
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (cancelled || moduleAuthResolved) return;
            
            console.log('🔐 AuthGatekeeper: Auth state changed:', firebaseUser?.uid || 'No user', 'isFirst:', isFirstCallback);
            
            // Clear any pending timeout
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
            
            if (firebaseUser) {
                // User is definitely logged in - proceed immediately
                isFirstCallback = false;
                
                try {
                    // User is signed in - verify with backend and get profile
                    const idToken = await firebaseUser.getIdToken();
                    
                    // Authenticate with backend
                    const authResponse = await fetch(`${API_URL}/api/auth/firebase`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ id_token: idToken })
                    });
                    
                    let userData = null;
                    let profileData = null;
                    
                    if (authResponse.ok) {
                        userData = await authResponse.json();
                        console.log('✅ AuthGatekeeper: User authenticated:', userData.user_id);
                        
                        // CRITICAL: Store session token for API calls
                        if (userData.session_token) {
                            localStorage.setItem('session_token', userData.session_token);
                            console.log('🔑 AuthGatekeeper: Session token stored');
                        }
                        
                        // Fetch profile data
                        const headers = {};
                        if (userData.session_token) {
                            headers['Authorization'] = `Bearer ${userData.session_token}`;
                        }
                        
                        const profileResponse = await fetch(`${API_URL}/api/profile`, {
                            method: 'GET',
                            credentials: 'include',
                            headers
                        });
                        
                        if (profileResponse.ok) {
                            const profileResult = await profileResponse.json();
                            if (profileResult.has_profile && profileResult.profile) {
                                console.log('📋 AuthGatekeeper: Profile loaded:', profileResult.profile.name);
                                profileData = profileResult.profile;
                                
                                // CRITICAL: Restore language BEFORE rendering the app
                                if (profileData.language_context) {
                                    const langCode = profileData.language_context.split('-')[0].toLowerCase();
                                    if (langCode === 'es' || langCode === 'en') {
                                        console.log(`🌐 AuthGatekeeper: Restoring language to: ${langCode}`);
                                        localStorage.setItem('astrum_language', langCode);
                                    }
                                }
                                
                                // CELESTIAL POLISH: Cache sun_sign for personalized loading messages
                                if (profileData.sun_sign) {
                                    localStorage.setItem('astral_sun_sign', profileData.sun_sign.toLowerCase());
                                    console.log(`☀️ AuthGatekeeper: Cached sun sign: ${profileData.sun_sign}`);
                                }
                            }
                        }
                    }
                    
                    resolveAuth({
                        user: userData,
                        profile: profileData,
                        isAuthenticated: !!userData
                    });
                    
                } catch (err) {
                    console.error('❌ AuthGatekeeper: Auth error:', err);
                    resolveAuth({ user: null, profile: null, isAuthenticated: false });
                }
                
            } else {
                // No user detected
                if (isFirstCallback) {
                    // CRITICAL: First callback with null could be a false negative
                    // Firebase persistence may not have restored the user yet
                    console.log('⏳ AuthGatekeeper: First callback null, waiting for persistence...');
                    isFirstCallback = false;
                    
                    timeoutId = setTimeout(async () => {
                        if (cancelled || moduleAuthResolved) return;
                        
                        // Check if we have a localStorage session token as fallback
                        const storedToken = localStorage.getItem('session_token');
                        if (storedToken) {
                            console.log('🔑 AuthGatekeeper: Found stored session token, validating...');
                            try {
                                // Validate session with backend
                                const meResponse = await fetch(`${API_URL}/api/auth/me`, {
                                    method: 'GET',
                                    headers: {
                                        'Authorization': `Bearer ${storedToken}`,
                                        'Content-Type': 'application/json'
                                    },
                                    credentials: 'include'
                                });
                                
                                if (meResponse.ok) {
                                    const userData = await meResponse.json();
                                    console.log('✅ AuthGatekeeper: Session restored from token:', userData.user_id);
                                    
                                    // Fetch profile data
                                    let profileData = null;
                                    const profileResponse = await fetch(`${API_URL}/api/profile`, {
                                        method: 'GET',
                                        headers: {
                                            'Authorization': `Bearer ${storedToken}`,
                                            'Content-Type': 'application/json'
                                        },
                                        credentials: 'include'
                                    });
                                    
                                    if (profileResponse.ok) {
                                        const profileResult = await profileResponse.json();
                                        if (profileResult.has_profile && profileResult.profile) {
                                            console.log('📋 AuthGatekeeper: Profile loaded:', profileResult.profile.name);
                                            profileData = profileResult.profile;
                                            
                                            // CRITICAL: Restore language BEFORE rendering the app
                                            if (profileData.language_context) {
                                                const langCode = profileData.language_context.split('-')[0].toLowerCase();
                                                if (langCode === 'es' || langCode === 'en') {
                                                    console.log(`🌐 AuthGatekeeper: Restoring language to: ${langCode}`);
                                                    localStorage.setItem('astrum_language', langCode);
                                                }
                                            }
                                            
                                            // CELESTIAL POLISH: Cache sun_sign for personalized loading messages
                                            if (profileData.sun_sign) {
                                                localStorage.setItem('astral_sun_sign', profileData.sun_sign.toLowerCase());
                                                console.log(`☀️ AuthGatekeeper: Cached sun sign: ${profileData.sun_sign}`);
                                            }
                                        }
                                    }
                                    
                                    resolveAuth({
                                        user: userData,
                                        profile: profileData,
                                        isAuthenticated: true
                                    });
                                    return;
                                } else {
                                    // Token invalid, clear it
                                    console.log('⚠️ AuthGatekeeper: Stored token invalid, clearing...');
                                    localStorage.removeItem('session_token');
                                }
                            } catch (err) {
                                console.error('❌ AuthGatekeeper: Token validation error:', err);
                                localStorage.removeItem('session_token');
                            }
                        }
                        
                        // If we get here, no valid session was found
                        console.log('👤 AuthGatekeeper: Confirmed guest (no persistence data)');
                        resolveAuth({ user: null, profile: null, isAuthenticated: false });
                    }, 800);
                    
                } else {
                    // Not the first callback - user actually logged out
                    console.log('👤 AuthGatekeeper: User logged out');
                    resolveAuth({ user: null, profile: null, isAuthenticated: false });
                }
            }
        });

        return () => {
            cancelled = true;
            if (timeoutId) clearTimeout(timeoutId);
            unsubscribe();
        };
    }, []);

    // Logout function that can be called from anywhere
    const logout = async (onComplete) => {
        try {
            // Clear local state FIRST for immediate UX feedback
            localStorage.removeItem('astral_belonging_state');
            localStorage.removeItem('astral_belonging_onboarding');
            localStorage.removeItem('session_token');
            // CELESTIAL POLISH: Clear personalized loading cache on logout
            localStorage.removeItem('astral_sun_sign');
            localStorage.removeItem('astral_loading_message');
            
            // Reset MODULE-LEVEL state so next login works
            moduleAuthResolved = false;
            moduleAuthResult = null;
            
            // Reset component state
            setUser(null);
            setProfile(null);
            setIsAuthenticated(false);
            
            // Sign out from Firebase - MUST complete before refresh
            try {
                const { signOut } = await import('firebase/auth');
                await signOut(auth);
                console.log('✅ Firebase signed out');
            } catch (err) {
                console.debug('Firebase signout:', err);
            }
            
            // Sign out from backend to invalidate session cookie
            try {
                await fetch(`${API_URL}/api/auth/logout`, {
                    method: 'POST',
                    credentials: 'include'
                });
                console.log('✅ Backend session cleared');
            } catch (err) {
                console.debug('Backend logout:', err);
            }
            
            console.log('✅ AuthGatekeeper: Logged out completely');
            
            if (onComplete) onComplete();
        } catch (err) {
            console.error('Logout error:', err);
            // Still clear state even if something fails
            moduleAuthResolved = false;
            moduleAuthResult = null;
            localStorage.removeItem('session_token');
            setUser(null);
            setProfile(null);
            setIsAuthenticated(false);
            if (onComplete) onComplete();
        }
    };

    const contextValue = {
        user,
        profile,
        isAuthenticated,
        isLoading,
        logout
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {/* The HTML initial-loader covers everything until auth resolves */}
            {/* We only render children AFTER auth check completes */}
            {!isLoading && children}
        </AuthContext.Provider>
    );
};

export default AuthGatekeeper;
